import type { SidebarItem } from '../../../components/sidebar/sidebar';
import React, { FC } from 'react';
import { Links, useLocation, useNavigate } from 'react-router';
import { useTranslations } from '../../../contexts/translation.context';
import {
  User,
  Badge,
  Avatar,
  Button,
  ScrollShadow,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Spacer,
} from '@heroui/react';
import { Icon } from '@iconify/react';

import NotificationsCard from '../../../components/sidebar/notifications-card';
import Sidebar from '../../../components/sidebar/sidebar';
import SidebarChip from '../../../components/sidebar/sidebar-chip';
import { useAuth } from '../../../contexts/AuthContext';
import Typography from '../../../components/typography/Typography';
import { Link } from 'react-router';
import {
  CostsSidebarIc,
  LogoPartnerSidebarIc,
  TransactionSidebarIc,
} from '../../../components/icons';
import IcTg from '../../../client/ic-telegram.svg';
export const GridIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M2.5 6.5C2.5 4.29086 4.29086 2.5 6.5 2.5C8.70914 2.5 10.5 4.29086 10.5 6.5C10.5 8.70914 8.70914 10.5 6.5 10.5C4.29086 10.5 2.5 8.70914 2.5 6.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M13.5 17.5C13.5 15.2909 15.2909 13.5 17.5 13.5C19.7091 13.5 21.5 15.2909 21.5 17.5C21.5 19.7091 19.7091 21.5 17.5 21.5C15.2909 21.5 13.5 19.7091 13.5 17.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M21.5 6.5C21.5 4.61438 21.5 3.67157 20.9142 3.08579C20.3284 2.5 19.3856 2.5 17.5 2.5C15.6144 2.5 14.6716 2.5 14.0858 3.08579C13.5 3.67157 13.5 4.61438 13.5 6.5C13.5 8.38562 13.5 9.32843 14.0858 9.91421C14.6716 10.5 15.6144 10.5 17.5 10.5C19.3856 10.5 20.3284 10.5 20.9142 9.91421C21.5 9.32843 21.5 8.38562 21.5 6.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M10.5 17.5C10.5 15.6144 10.5 14.6716 9.91421 14.0858C9.32843 13.5 8.38562 13.5 6.5 13.5C4.61438 13.5 3.67157 13.5 3.08579 14.0858C2.5 14.6716 2.5 15.6144 2.5 17.5C2.5 19.3856 2.5 20.3284 3.08579 20.9142C3.67157 21.5 4.61438 21.5 6.5 21.5C8.38562 21.5 9.32843 21.5 9.91421 20.9142C10.5 20.3284 10.5 19.3856 10.5 17.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);
const DashboardIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <GridIcon
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.dashboardIcon')}
    />
  );
});
DashboardIcon.displayName = 'DashboardIcon';

const SubscribersIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Icon
      icon="solar:users-group-two-rounded-outline"
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.subscribersIcon')}
    />
  );
});
SubscribersIcon.displayName = 'SubscribersIcon';

const OffersIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Icon
      icon="solar:bill-list-outline"
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.offersIcon')}
    />
  );
});
OffersIcon.displayName = 'OffersIcon';

const StatisticsIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Icon
      icon="solar:pie-chart-2-outline"
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.statisticsIcon')}
    />
  );
});
StatisticsIcon.displayName = 'StatisticsIcon';

const AccountIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Icon
      icon="solar:settings-outline"
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.accountIcon')}
    />
  );
});
AccountIcon.displayName = 'AccountIcon';

const PartnerSalesChip = React.memo(() => {
  const { partnerOffersLength, partnerActiveOffersLength } = useAuth();
  const { t } = useTranslations();
  const active = Number(partnerActiveOffersLength) || 0;
  const total = Number(partnerOffersLength) || 0;
  return (
    <SidebarChip ariaLabel={`${t('sidebar.subscribersSales')}: ${active} / ${total}`}>
      {active} / {total}
    </SidebarChip>
  );
});
PartnerSalesChip.displayName = 'PartnerSalesChip';

const PartnerOrdersChip = React.memo(() => {
  const { partnerOrdersLength } = useAuth();
  const { t } = useTranslations();
  const total = Number(partnerOrdersLength) || 0;
  return (
    <SidebarChip ariaLabel={`${t('sidebar.subscribersOrders')}: ${total}`}>{total}</SidebarChip>
  );
});
PartnerOrdersChip.displayName = 'PartnerOrdersChip';

const WalletChip = React.memo(() => {
  const { user } = useAuth();
  const { t } = useTranslations();
  return (
    <SidebarChip ariaLabel={`${t('sidebar.balance')}: $${user?.balance?.toFixed(2) || '0.00'}`}>
      ${user?.balance?.toFixed(2) || '0.00'}
    </SidebarChip>
  );
});
WalletChip.displayName = 'WalletChip';

const RevshareOffersChip = React.memo(() => {
  const { partnerRevshareOffersLength } = useAuth();
  const { t } = useTranslations();
  const total = Number(partnerRevshareOffersLength) || 0;
  console.log('RevshareOffersChip render, count:', partnerRevshareOffersLength, 'total:', total);
  return <SidebarChip ariaLabel={`${t('sidebar.offers')}: ${total}`}>{total}</SidebarChip>;
});
RevshareOffersChip.displayName = 'RevshareOffersChip';

const RevshareAccountsChip = React.memo(() => {
  const { partnerRevshareAccountsLength } = useAuth();
  const { t } = useTranslations();
  const total = Number(partnerRevshareAccountsLength) || 0;
  return (
    <SidebarChip ariaLabel={`${t('sidebar.onlyfansAccounts')}: ${total}`}>{total}</SidebarChip>
  );
});
RevshareAccountsChip.displayName = 'RevshareAccountsChip';

const RevshareCampaignsChip = React.memo(() => {
  const { partnerRevshareCampaignsLength } = useAuth();
  const { t } = useTranslations();
  const total = Number(partnerRevshareCampaignsLength) || 0;
  return (
    <SidebarChip ariaLabel={`${t('sidebar.revshareCampaigns')}: ${total}`}>{total}</SidebarChip>
  );
});
RevshareCampaignsChip.displayName = 'RevshareCampaignsChip';

const getSidebarItems = (t: (key: string) => string): SidebarItem[] => [
  {
    key: 'dashboard',
    href: '/partner/dashboard',
    startContent: <DashboardIcon />,
    title: t('sidebar.dashboard'),
  },
  {
    key: 'subscribers',
    title: t('sidebar.subscribersCPL'),
    startContent: <SubscribersIcon />,
    items: [
      {
        key: 'subscribers-sales',
        href: '/partner/subscribers/sales',
        icon: 'solar:checklist-minimalistic-outline',
        title: t('sidebar.subscribersSales'),
        endContent: <PartnerSalesChip />,
      },
      {
        key: 'subscribers-orders',
        href: '/partner/subscribers/orders',
        icon: 'solar:cart-large-minimalistic-outline',
        title: t('sidebar.subscribersOrders'),
        endContent: <PartnerOrdersChip />,
      },
    ],
  },
  {
    key: 'revshare',
    title: t('sidebar.offersRevshare'),
    startContent: <OffersIcon />,
    items: [
      {
        key: 'revshare-offers',
        href: '/partner/revshare/offers',
        icon: 'solar:bill-list-outline',
        title: t('sidebar.offers'),
        endContent: <RevshareOffersChip />,
      },
      {
        key: 'revshare-accounts',
        href: '/partner/revshare/accounts',
        icon: 'solar:lock-unlocked-outline',
        title: t('sidebar.onlyfansAccounts'),
        endContent: <RevshareAccountsChip />,
      },
      {
        key: 'revshare-campaigns',
        href: '/partner/revshare/campaigns',
        icon: 'solar:star-outline',
        title: t('sidebar.revshareCampaigns'),
        endContent: <RevshareCampaignsChip />,
      },
      {
        key: 'revshare-subscribers',
        href: '/partner/revshare/subscribers',
        icon: 'solar:users-group-two-rounded-outline',
        title: t('sidebar.revshareSubscribers'),
      },
      {
        key: 'revshare-transactions',
        href: '/partner/revshare/transactions',
        icon: 'solar:card-transfer-linear',
        title: t('sidebar.revshareTransactions'),
      },
      {
        key: 'revshare-costs',
        href: '/partner/revshare/costs',
        icon: 'solar:card-send-outline',
        title: t('sidebar.revshareCosts'),
      },
      {
        key: 'revshare-income',
        href: '/partner/revshare/income',
        icon: 'solar:dollar-outline',
        title: t('sidebar.revshareIncome'),
      },
    ],
  },
  {
    key: 'statistics',
    title: t('sidebar.statistics'),
    startContent: <StatisticsIcon />,
    items: [
      {
        key: 'statistics-campaigns',
        href: '/partner/statistics/campaigns',
        icon: 'solar:pie-chart-2-outline',
        title: t('sidebar.campaigns'),
      },
    ],
  },
  {
    key: 'account',
    title: t('sidebar.account'),
    startContent: <AccountIcon />,
    items: [
      {
        key: 'account-settings',
        href: '/partner/account/settings',
        icon: 'solar:settings-outline',
        title: t('sidebar.settings'),
      },
      {
        key: 'account-wallet',
        href: '/partner/account/wallet',
        icon: 'solar:wallet-linear',
        title: t('sidebar.wallet'),
        endContent: <WalletChip />,
      },
    ],
  },
];

const SidebarActiveTracker = React.memo<{ onItemClick?: () => void }>(({ onItemClick }) => {
  const location = useLocation();
  const deferredPathname = React.useDeferredValue(location.pathname);
  const { t } = useTranslations();

  const sidebarItems = React.useMemo(() => getSidebarItems(t), [t]);

  const selectedKey = React.useMemo(() => {
    let bestMatch = { key: 'dashboard', matchLength: 0 };

    for (const item of sidebarItems) {
      if (item.href) {
        if (deferredPathname === item.href) {
          return item.key;
        } else if (deferredPathname.startsWith(item.href + '/')) {
          if (item.href.length > bestMatch.matchLength) {
            bestMatch = { key: item.key, matchLength: item.href.length };
          }
        }
      }
      if (item.items) {
        for (const subItem of item.items) {
          if (subItem.href) {
            if (deferredPathname === subItem.href) {
              return subItem.key;
            } else if (deferredPathname.startsWith(subItem.href + '/')) {
              if (subItem.href.length > bestMatch.matchLength) {
                bestMatch = { key: subItem.key, matchLength: subItem.href.length };
              }
            }
          }
        }
      }
    }

    return bestMatch.key;
  }, [deferredPathname]);

  return (
    <SidebarContent
      selectedKey={selectedKey}
      sidebarItems={sidebarItems}
      onItemClick={onItemClick}
    />
  );
});

const SidebarContent = React.memo<{
  selectedKey: string;
  sidebarItems: SidebarItem[];
  onItemClick?: () => void;
}>(({ selectedKey, sidebarItems, onItemClick }) => {
  const [isPending, startTransition] = React.useTransition();
  const { user } = useAuth();

  const sidebarProps = React.useMemo(
    () => ({
      iconClassName: '',
      items: sidebarItems,
      selectedKey,
      isPending,
      startTransition,
      userRole: user?.role,
    }),
    [selectedKey, isPending, user?.role, sidebarItems]
  );

  return <Sidebar {...sidebarProps} onItemClick={onItemClick} />;
});

SidebarContent.displayName = 'SidebarContent';

const getInitials = (name: any) => {
  if (!name) return '';

  const words = name.trim().split(' ');

  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  } else if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }

  return '';
};

const SidebarHeader = React.memo<{ onClose?: () => void }>(({ onClose }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { user, notifications } = useAuth();
  const { t } = useTranslations();
  const handleNavigate = React.useCallback(
    (path: string) => {
      onClose?.();
      navigate(path);
    },
    [navigate, onClose]
  );
  const handleLogout = React.useCallback(() => {
    onClose?.();
    logout();
  }, [logout, onClose]);

  return (
    <div className="flex items-center justify-between md:pt-0 pt-7">
      <div className="flex items-center gap-2 md:px-2">
        <Link to="/partner/dashboard" className="icon h-[1.375rem] w-[4.75rem]">
          <LogoPartnerSidebarIc />
        </Link>
        <span className="text-xs font-regular text-auth-muted select-none">
          {t('sidebar.marketer')}
        </span>
      </div>
      <div className="flex items-center justify-end">
        <Popover offset={12} placement="bottom-start" aria-label={t('aria.notificationsPanel')}>
          <PopoverTrigger>
            <Button
              disableRipple
              isIconOnly
              className="overflow-visible"
              radius="full"
              variant="light"
              aria-label={t('sidebar.notifications')}
            >
              {(notifications?.objects?.filter((n: any) => !n.was_read).length || 0) > 0 ? (
                <Badge
                  color="danger"
                  className="bg-dangerDeep w-4 top-[0.1875rem] right-[0.3125rem] h-4"
                  content={notifications?.objects?.filter((n: any) => !n.was_read).length}
                  showOutline={false}
                  size="sm"
                  aria-label="Уведомления"
                  classNames={{
                    base: 'text-white text-xxs leading-4 font-regular',
                  }}
                >
                  <Icon
                    className="text-default-500"
                    icon="solar:bell-outline"
                    width={24}
                    height={24}
                    aria-label={t('sidebar.notifications')}
                  />
                </Badge>
              ) : (
                <Icon
                  className="text-default-500"
                  icon="solar:bell-outline"
                  width={24}
                  height={24}
                  aria-label={t('sidebar.notifications')}
                />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-w-[90vw] p-0 md:max-w-[27.4375rem]">
            <NotificationsCard className="w-full shadow-none" />
          </PopoverContent>
        </Popover>
        <Dropdown showArrow placement="bottom-start" aria-label={t('sidebar.userMenu')}>
          <DropdownTrigger>
            <Button
              disableRipple
              isIconOnly
              className="-mr-1"
              radius="full"
              variant="light"
              aria-label={t('sidebar.userMenu')}
            >
              <div className="border border-default-300 rounded-full p-0.5">
                <Avatar
                  className="h-7 w-7 cursor-pointer"
                  name={getInitials(user?.name)}
                  src={user?.avatar}
                  aria-label={`${t('sidebar.profile')} ${user?.name}`}
                />
              </div>
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Custom item styles" disabledKeys={['profile']}>
            <DropdownSection aria-label="Profile & Actions">
              <DropdownItem
                key="profile"
                isReadOnly
                className="h-14 gap-2 opacity-100"
                textValue="Signed in as"
              >
                <User
                  avatarProps={{
                    size: 'sm',
                    imgProps: {
                      className: 'transition-none',
                    },
                    src: user?.avatar,
                    name: getInitials(user?.name),
                  }}
                  classNames={{
                    name: 'text-default-600',
                    description: 'text-default-500',
                  }}
                  name={user?.name}
                  aria-label={`${t('sidebar.profile')} ${user?.name}`}
                />
              </DropdownItem>
              <DropdownItem
                onClick={() => handleNavigate('/partner/account/wallet')}
                endContent={<WalletChip />}
                key="balance"
              >
                {t('sidebar.balance')}
              </DropdownItem>
              <DropdownItem
                onClick={() => handleNavigate('/partner/account/settings')}
                key="settings"
              >
                {t('sidebar.settings')}
              </DropdownItem>
              <DropdownItem key="logout" onClick={handleLogout}>
                {t('sidebar.logout')}
              </DropdownItem>
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
        <button
          type="button"
          aria-label={t('aria.closeSidebar')}
          className="ml-2 flex size-8 items-center justify-center rounded-full text-default-500 hover:bg-default-200/60 md:hidden"
          onClick={onClose}
        >
          <Icon icon="solar:close-circle-linear" width={20} />
        </button>
      </div>
    </div>
  );
});

SidebarHeader.displayName = 'SidebarHeader';

const Component = React.memo<{ onItemClick?: () => void; onClose?: () => void }>(
  ({ onItemClick, onClose }) => {
    const { t } = useTranslations();
    const isPartner = true;
    return (
      <div className="h-full min-h-[48rem]">
        <div className="relative flex h-full w-full md:w-[18.1875rem] flex-1 flex-col md:border-r-small md:border-divider pt-0 pb-8 px-4 md:py-6">
          <SidebarHeader onClose={onClose} />
          <div
            className="md:-mr-6 h-full md:max-h-full py-6 md:pr-6"
            aria-label={t('aria.mainNavigationMenu')}
          >
            <SidebarActiveTracker onItemClick={onItemClick} />
            <Spacer y={8} />
          </div>
          <div className="mt-auto pt-6 md:hidden">
            <a
              href={isPartner ? 'https://t.me/onlyu_partners' : 'https://t.me/onlyu_support'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                startContent={<img src={IcTg} alt={t('alt.telegram')} />}
                className="h-10 rounded-3 border-[0.125rem] border-auth-strength-none text-auth-ink text-sm font-semibold leading-5 flex items-center justify-center text-center gap-1 bg-white w-full"
                style={{ boxShadow: 'none' }}
              >
                {t('buttons.support')}
              </Button>
            </a>
          </div>
        </div>
      </div>
    );
  }
);

Component.displayName = 'Component';

export default Component;
